<template>
  <b-container v-if="codigo && estado" class="tk-wrap">
    <!-- Ya canjeado en esta visita: la celebración reemplaza la tarjeta. -->
    <div v-if="celebrando" class="tk-lluvia" aria-hidden="true">
      <span
        v-for="(c, i) in confetti"
        :key="i"
        class="tk-piece"
        :style="{
          left: c.left,
          background: c.color,
          width: c.ancho,
          height: c.alto,
          borderRadius: c.radio,
          animationDelay: c.delay,
          animationDuration: c.dur,
          '--tk-giro': c.giro,
        }"
      ></span>
    </div>
    <div v-if="celebrando" class="tk-card tk-win">
      <div class="tk-main">
        <div class="tk-emoji">🎟️</div>
        <h3 class="tk-title">¡Listo! Tu golden ticket ya está canjeado</h3>
        <p class="tk-sub">
          Sumamos <strong>{{ usosLabel }}</strong> de corrección de tests
          <span v-if="cuenta">a la cuenta <strong>{{ cuenta }}</strong></span>
          <span v-else>a tu cuenta</span>. Ya puedes empezar a corregir.
        </p>
      </div>
    </div>

    <!-- Vivo: la tarjeta que ve quien acaba de escanear el QR. -->
    <div v-else-if="estado === 'vivo'" class="tk-card tk-vivo">
      <div class="tk-main">
        <div class="tk-head">
          <span class="tk-avatar">🎟️</span>
          <span class="tk-chip">Golden ticket</span>
        </div>
        <h3 class="tk-title">
          <strong>{{ encargado }}</strong
          >, el encargado de Utopie, te dio un ticket dorado
        </h3>
        <p class="tk-sub">
          Trae <strong>{{ usosLabel }} de regalo</strong> para corregir tus
          tests. Canjéalos ingresando aquí.
        </p>
        <div class="tk-badges">
          <span class="tk-badge">🎁 {{ usosLabel }} para ti</span>
          <span class="tk-badge" v-if="vence">Vence el {{ vence }}</span>
        </div>
      </div>
      <div class="tk-cta">
        <button type="button" class="tk-btn" @click="pedirSesion">
          Iniciar sesión y canjear mi ticket
        </button>
        <p class="tk-hint">Con tu cuenta de Google · sin contraseñas</p>
      </div>
    </div>

    <!-- Quemado, vencido o inventado: la MISMA tarjeta, que es la de
         conversión. Con el tiempo es la que más se ve: los códigos se queman
         pero los PDF siguen circulando. -->
    <div v-else class="tk-card tk-usado">
      <div class="tk-main">
        <div class="tk-head">
          <span class="tk-avatar tk-gris">🎟️</span>
          <span class="tk-chip tk-chip-gris">Golden ticket</span>
        </div>
        <h3 class="tk-title">¡Bienvenido/a a <strong>Utopie</strong>!</h3>
        <p class="tk-sub">{{ explicacion }}</p>
        <p class="tk-sub">
          Te podemos mandar un ticket personalizado solo para ti.
          <strong>¿Te gustaría recibir {{ usosLabel }} gratis para corregir
          tus tests?</strong> Sigue estos pasos:
        </p>
        <ol class="tk-pasos">
          <li>
            Sigue nuestra cuenta en Instagram:
            <a
              :href="INSTAGRAM"
              target="_blank"
              rel="noopener"
              @click="contarInstagram('cuenta')"
              >{{ CUENTA }}</a
            >
          </li>
          <li>
            Dale me gusta a
            <a
              v-if="postUrl"
              :href="postUrl"
              target="_blank"
              rel="noopener"
              @click="contarInstagram('publicacion')"
              >esta publicación</a
            >
            <span v-else>la publicación de la campaña</span>.
          </li>
          <li>
            Escribe un comentario con la palabra
            <strong v-if="palabra">{{ palabra }}</strong>
            <span v-else>de la campaña</span> y etiqueta a dos colegas en él.
          </li>
        </ol>
        <p class="tk-sub tk-sub-fin">
          Te enviamos por mensaje directo tu ticket personal.
        </p>
      </div>
    </div>
  </b-container>
</template>

<script>
// Tarjeta de quien llega con un golden ticket
// (`utopie.cl/c/<codigo>`, que Django redirige a
// `/utopie/#/test-correction-demo?ticket=<codigo>`).
//
// Calca el recorrido de InvitationBanner, que es el que ya resolvió la parte
// difícil: el código se aparca en localStorage APENAS se lee, así sobrevive el
// ida y vuelta del login de Google, y el canje se dispara solo en cuanto
// aparece la sesión —venga la persona de iniciarla aquí o llegue ya logueada.
//
// La diferencia con la invitación es el estado. El token de invitación es una
// firma sin estado y se puede usar infinitas veces; el ticket es al portador y
// se quema en el primer canje, así que el backend es quien manda: `ticket_info`
// dice en cuál de los cuatro estados está antes de pedirle nada a nadie.
import ticketService from "@/services/ticketService";
import {
  eventoLogin,
  medirTicketCanjeado,
  medirTicketInstagram,
  medirTicketVisto,
} from "@/services/analytics";
import { recordarOrigenLogin } from "@/services/origen";
import { marcarTicketVisible } from "./ticketFocus";

const STORAGE_KEY = "ticket_code";

// De dónde salió este muro de login. Es un valor nuevo del vocabulario de
// `origen_login` (que traía `trial`, `demo_button` y `service`), y existe
// porque hasta ahora esta tarjeta abría el modal SIN emitir muro: los
// `login_click` / `login_attempt` / `login_success` que venían después salían
// sin origen —o, peor, heredando el de un muro de demo que la persona había
// visto antes en la misma carga, que es atribuirle al muro equivocado una
// conversión que trajo el ticket.
const ORIGEN_LOGIN = "ticket";

// El código de otro ticket, ya muerto, que esta persona vio antes. Va en su
// PROPIA clave y no en STORAGE_KEY: esa es el ticket que se está canjeando y
// se borra apenas deja de servir, y son dos cosas con vidas distintas.
const VISTO_KEY = "ticket_visto";

// Cuánto vale un ticket visto. Treinta días es lo que tarda en dejar de ser
// verdad que "esta persona llegó por aquel ticket": más allá, el enlace dice
// menos de la campaña que del navegador que no se limpió.
const DIAS_DEL_VISTO = 30;

// localStorage puede lanzar (ventana privada, cookies bloqueadas). Nada de lo
// que hace esta tarjeta vale una pantalla en blanco.
const guardar = (valor) => {
  try {
    localStorage.setItem(STORAGE_KEY, valor);
  } catch (e) {
    /* sin almacenamiento el ticket no sobrevive al login, pero la página sí */
  }
};
const leer = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) || "";
  } catch (e) {
    return "";
  }
};
const olvidarGuardado = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    /* nada que limpiar */
  }
};

// El ticket ajeno que esta persona vio. Se guarda con fecha para poder
// caducarlo: sin eso, un navegador que vio una tarjeta muerta en octubre
// seguiría atribuyéndole el canje de marzo.
const recordarVisto = (codigo) => {
  try {
    localStorage.setItem(
      VISTO_KEY,
      JSON.stringify({ codigo: codigo, ts: Date.now() })
    );
  } catch (e) {
    /* sin almacenamiento no se enlaza, y la tarjeta funciona igual */
  }
};

const leerVisto = () => {
  try {
    const crudo = localStorage.getItem(VISTO_KEY);
    if (!crudo) return "";
    const dato = JSON.parse(crudo);
    const dias = (Date.now() - Number(dato.ts || 0)) / 86400000;
    return dias >= 0 && dias <= DIAS_DEL_VISTO ? String(dato.codigo || "") : "";
  } catch (e) {
    // Vale también para un JSON roto: lo que no se entiende, no se usa.
    return "";
  }
};

const olvidarVisto = () => {
  try {
    localStorage.removeItem(VISTO_KEY);
  } catch (e) {
    /* nada que limpiar */
  }
};

// El código se captura al CARGAR EL MÓDULO, no en `created()`, y esto no es un
// detalle de estilo.
//
// Entre que arranca el SPA y que esta tarjeta se monta puede haber una
// redirección que se lleve la query puesta. La real: `HeaderComponent` pide los
// datos del usuario si hay un token guardado, y si el backend responde 403
// —sesión vencida, que es el caso de cualquiera que vuelva días después— llama
// a `handleForbidden`, que empuja a la portada. Ahí `$route.query.ticket` ya no
// existe y el ticket se pierde en silencio: la persona escaneó su QR, aterrizó
// en la portada genérica y nadie se entera de que se quedó sin sus usos.
//
// Leyendo `window.location` al importar el módulo, el código queda guardado
// antes de que el router pueda navegar a ningún lado.
const codigoDeLaUrl = () => {
  const crudo = String(window.location.hash || "") + String(window.location.search || "");
  // El `?` también corta. `crudo` pega el hash con la query real, y el
  // redirect pone las utm en la query: sin el `?` en la clase, el código
  // se leía como `CODIGO?utm_source=qr` y el servidor respondía «no existe».
  const m = /[?&]ticket=([^&#?]+)/.exec(crudo);
  if (!m) return "";
  try {
    return decodeURIComponent(m[1]);
  } catch (e) {
    return m[1];
  }
};
const CODIGO_INICIAL = codigoDeLaUrl();
if (CODIGO_INICIAL) guardar(CODIGO_INICIAL);

// Quien firma el regalo. Va en una constante y no suelto en la plantilla
// porque es lo primero que va a querer cambiar quien tome esto después.
const ENCARGADO = "Adolfo";

// La cuenta a la que se pide seguir. Va acá por la misma razón que ENCARGADO:
// es lo segundo que alguien va a querer cambiar.
const CUENTA = "@plataforma_pie";
const INSTAGRAM = "https://www.instagram.com/plataforma_pie/";

// La paleta de la celebracion de compra (TransactionSuccess), pasteles
// incluidos: cada color fuerte va con su version clara, y eso es la mitad de
// por que esa lluvia se ve rica y no como seis colores repitiendose.
const CONFETTI_COLORS = [
  "#20b2aa", "#84cec7",
  "#c6588d", "#e8b5d1",
  "#f8ac04", "#f9d38f",
  "#e84756", "#ecd5cd",
];

// Los tres estados terminales pintan la misma tarjeta. Solo cambia el titular,
// para no decirle "ya fue canjeado" a quien en realidad llegó tarde.
// El PDF se reenvía a grupos de WhatsApp: la mayoría de quienes escaneen un
// código ya quemado NO son su dueño. Esa pantalla no es un error, es la puerta
// de entrada de esa gente, así que explica en una línea qué pasó y sigue
// derecho a los tres pasos.
// Por qué este ticket no sirve. Sin el nombre de quien lo canjeó: el PDF se
// reenvía a grupos, así que esta pantalla la ve gente que no conoce a esa
// persona, y nombrarla no le aporta nada a quien llega — solo expone a alguien
// que no eligió aparecer acá.
const EXPLICACION = {
  canjeado: (u) =>
    `Estás probando un ticket dorado de ${u} que ya fue usado por otro usuario y por ende está caduco.`,
  vencido: (u) =>
    `Estás probando un ticket dorado de ${u} que ya venció y por ende está caduco.`,
  invalido: () =>
    "No encontramos este ticket dorado: puede estar mal copiado, o ya no existir.",
};

export default {
  name: "TicketBanner",
  data() {
    return {
      codigo: "",
      estado: "",
      usos: 0,
      vence: "",
      palabra: "",
      postUrl: "",
      celebrando: false,
      // En vuelo: `created` y el watch de la sesion son dos caminos hacia el
      // mismo cobro y podrian solaparse.
      cobrando: false,
      confetti: [],
    };
  },
  created() {
    // `$route.query` sigue valiendo para una navegación dentro de la app, que
    // no pasa por la carga del módulo.
    const codigo = CODIGO_INICIAL || this.$route.query.ticket || leer();
    if (!codigo) return;
    guardar(codigo);
    this.codigo = codigo;
    ticketService.info(codigo).then(
      (data) => {
        this.estado = data.estado || "invalido";
        this.usos = Number(data.usos) || 0;
        this.vence = this.fechaHumana(data.vence);
        this.palabra = data.palabra || "";
        this.postUrl = data.post_url || "";
        // Los cuatro estados cuentan, y cuentan acá: es el único punto por el
        // que pasan todos. El estado que se manda es el PRIMERO que se supo
        // de este código; si después cambia (un 409 en el canje lo pasa a
        // `canjeado`) no se vuelve a contar, porque es el mismo escaneo.
        medirTicketVisto(this.estado, this.codigo);
        // Un ticket que ya no sirve no tiene por qué seguir a nadie de página
        // en página: se muestra la tarjeta de esta visita y se olvida.
        if (this.estado !== "vivo") {
          olvidarGuardado();
          // Pero sí se recuerda que esta persona vio uno muerto. Si canjea
          // otro dentro de 30 días, eso dice que la tarjeta de «consigue el
          // tuyo» funcionó, que es la pregunta de negocio entera.
          recordarVisto(this.codigo);
        } else {
          this.anunciarMuro();
          // Que la demo no se lleve el scroll mientras el ticket siga sin
          // canjear, y traer la tarjeta a la vista una vez ya renderizada.
          marcarTicketVisible(true);
          this.$nextTick(() => this.mostrarTarjeta());
          this.cobrar();
        }
      },
      // Si la consulta falla no se inventa un estado: mejor ninguna tarjeta
      // que una que promete algo que no podemos confirmar.
      () => this.olvidar()
    );
  },
  watch: {
    // La persona inicia sesión con la tarjeta en pantalla: cobrar sin recargar.
    "$store.state.login.token"(value) {
      if (value) {
        this.cobrar();
        return;
      }
      // Cerró sesión. La celebración era de la sesión anterior y no puede
      // quedarse en pantalla: quien mire después vería «ya está canjeado»
      // sobre una cuenta que ya no es la suya. Se vuelve a preguntar al
      // servidor y se muestra lo que diga.
      if (this.celebrando && this.codigo) {
        this.celebrando = false;
        ticketService.info(this.codigo).then(
          (data) => {
            this.estado = data.estado || "canjeado";
          },
          () => this.olvidar()
        );
      }
    },
  },
  computed: {
    encargado: () => ENCARGADO,
    CUENTA: () => CUENTA,
    INSTAGRAM: () => INSTAGRAM,
    // A qué cuenta fueron los usos. Quien llega con la sesión ya iniciada no
    // pasa por el login, así que sin decirlo no tiene cómo saber a dónde
    // fueron a parar — ni siquiera que el canje ocurrió.
    cuenta() {
      const s = this.$store.state.login;
      return s.email || s.first_name || "";
    },
    explicacion() {
      const f = EXPLICACION[this.estado] || EXPLICACION.invalido;
      return f(this.usosLabel);
    },
    usosLabel() {
      return `${this.usos} ${this.usos === 1 ? "uso" : "usos"}`;
    },

  },
  beforeDestroy() {
    marcarTicketVisible(false);
  },
  methods: {
    // El muro de login de esta tarjeta.
    //
    // Se anuncia al PINTARSE la tarjeta viva —que es cuando la persona ve el
    // botón de entrar—, no al apretarlo, igual que GoogleTrialButton lo hace
    // en su `mounted`. Y solo si no hay sesión: con sesión, `cobrar()` canjea
    // solo y nadie ve ningún muro.
    anunciarMuro() {
      if (this.$store.state.login.token) return;
      eventoLogin("login_wall_shown", { origen_login: ORIGEN_LOGIN });
    },
    pedirSesion() {
      // Otra vez, y no es de más: `olvidarOrigenLogin()` corre cada vez que se
      // cierra CUALQUIER modal (Default.vue, checkAndChange), y entre que esta
      // tarjeta pinta y la persona aprieta el botón puede haberse abierto y
      // cerrado otro. Sin esto, el login que sigue sale sin origen.
      recordarOrigenLogin(ORIGEN_LOGIN);
      this.$emit("open-login");
    },
    contarInstagram(cual) {
      medirTicketInstagram(cual);
    },
    olvidar() {
      marcarTicketVisible(false);
      olvidarGuardado();
      this.codigo = "";
      this.estado = "";
    },
    mostrarTarjeta() {
      const el = this.$el && this.$el.querySelector(".tk-card");
      // `scrollIntoView` no existe en jsdom ni en navegadores muy viejos: si no
      // esta, no pasa nada, la tarjeta igual esta arriba de la pagina.
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    },
    fechaHumana(iso) {
      if (!iso) return "";
      const [a, m, d] = String(iso).split("-");
      if (!a || !m || !d) return "";
      return `${Number(d)}-${Number(m)}-${a}`;
    },
    // El cobro es de una sola vez por ticket, y el backend es quien lo hace
    // cumplir: si dos pestañas lo intentan a la vez, una recibe 409 y cae en la
    // tarjeta de "ya fue canjeado". Por eso acá no hay más guardas que las
    // mínimas para no pedir dos veces desde la misma pantalla.
    cobrar() {
      if (this.celebrando || this.cobrando) return;
      if (!this.codigo || this.estado !== "vivo") return;
      if (!this.$store.state.login.token) return;
      this.cobrando = true;
      // Se lee ANTES de canjear: el canje borra la marca, y el `method` del
      // evento tiene que saber si la había.
      const desde = leerVisto();
      ticketService.redeem(this.codigo, desde).then(
        () => {
          this.cobrando = false;
          marcarTicketVisible(false);
          olvidarGuardado();
          // La marca ya cumplió: quedó en el ticket, del lado del servidor.
          // Dejarla puesta enlazaría también el próximo canje de esta persona.
          olvidarVisto();
          medirTicketCanjeado(
            this.usos,
            desde ? "tras_ticket_usado" : "directo"
          );
          this.confetti = this.armarConfetti();
          this.celebrando = true;
        },
        (error) => {
          this.cobrando = false;
          marcarTicketVisible(false);
          olvidarGuardado();
          const status = error.response && error.response.status;
          // 409 lo quemó otra persona · 410 venció · 404 no existe. Cualquier
          // otra cosa es un error nuestro y ahí no se muestra nada, porque el
          // ticket puede seguir siendo válido y no queremos decir lo contrario.
          if (status === 409) this.estado = "canjeado";
          else if (status === 410) this.estado = "vencido";
          else if (status === 404) this.estado = "invalido";
          else this.olvidar();
        }
      );
    },
    // Portado de `generateConfetti` en TransactionSuccess.vue. Lo que hace que
    // se vea bien no es una sola cosa: son 80 piezas en vez de 40, el color al
    // azar (no rotando en orden, que se nota), tres formas, tamanos distintos
    // y un giro propio por pieza. Sin esa variedad se ve una grilla cayendo.
    armarConfetti() {
      const piezas = [];
      for (let i = 0; i < 80; i += 1) {
        const tam = 6 + Math.random() * 8;
        const forma = Math.random();
        piezas.push({
          left: (Math.random() * 100).toFixed(2) + "%",
          color:
            CONFETTI_COLORS[
              Math.floor(Math.random() * CONFETTI_COLORS.length)
            ],
          ancho: tam.toFixed(1) + "px",
          alto: (tam * 1.4).toFixed(1) + "px",
          // Redondo, rectangulo con esquinas o papelito cuadrado.
          radio: forma < 0.33 ? "50%" : forma < 0.66 ? "2px" : "0",
          // Media vuelta para un lado o dos para el otro: sin esto todas las
          // piezas giran igual y se delata que es una animacion sola.
          giro: (Math.random() < 0.5 ? -1 : 1) *
            Math.floor(360 + Math.random() * 720) + "deg",
          delay: (0.1 + Math.random() * 2).toFixed(2) + "s",
          dur: (3 + Math.random() * 2.5).toFixed(2) + "s",
        });
      }
      return piezas;
    },
  },
};
</script>

<style lang="scss" scoped>
.tk-wrap {
  margin-bottom: 1.5rem;
}
.tk-card {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1.5rem;
  border-radius: 18px;
  padding: 1.5rem;
  text-align: left;
  overflow: hidden;
}
.tk-vivo {
  border: 2px dashed #f5a623;
  background: #fffaf0;
}
.tk-win {
  border: 2px solid #f5a623;
  background: #fffaf0;
  text-align: center;
}
.tk-usado {
  border: 2px solid #e3e3e0;
  background: #f7f7f5;
}
.tk-main {
  flex: 1 1 320px;
  position: relative;
  z-index: 1;
}
.tk-head {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.75rem;
}
.tk-avatar {
  width: 2.6rem;
  height: 2.6rem;
  border-radius: 50%;
  background: #f5a623;
  font-size: 1.3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  &.tk-gris {
    background: #dededa;
  }
}
.tk-chip {
  border: 1px solid #f5a623;
  border-radius: 999px;
  padding: 0.15rem 0.75rem;
  font-size: 0.7rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #9a6208;
  &.tk-chip-gris {
    border-color: #c9c9c4;
    color: #77776f;
  }
}
.tk-title {
  font-weight: 400;
  margin-bottom: 0.5rem;
  strong {
    color: #9a6208;
  }
}
.tk-sub {
  margin-bottom: 0.75rem;
}
.tk-sub-fin {
  margin-bottom: 0;
}
.tk-pasos {
  margin: 0 0 0.75rem;
  padding-left: 1.1rem;
  li {
    margin-bottom: 0.2rem;
  }
}
.tk-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.tk-badge {
  border: 1px solid #f0dfbe;
  border-radius: 10px;
  padding: 0.35rem 0.75rem;
  background: white;
  font-size: 0.9rem;
}
.tk-cta {
  flex: 0 1 320px;
  text-align: center;
  position: relative;
  z-index: 1;
}
.tk-btn {
  width: 100%;
  border: 1px solid #f5a623;
  border-radius: 12px;
  background: white;
  color: #9a6208;
  font-weight: 700;
  padding: 0.9rem 1rem;
}
.tk-hint {
  font-size: 0.8rem;
  color: #7b756c;
  margin: 0.5rem 0 0;
}
.tk-emoji {
  font-size: 2.4rem;
  line-height: 1;
  margin-bottom: 0.4rem;
}

/* Confeti portado de la celebración de compra (TransactionSuccess.vue).
   Se mantiene una copia propia en vez de extraerlo a un módulo compartido:
   tocar esa pantalla o el modal de login —por donde entra todo el mundo— no
   vale el riesgo por una animación.

   Cae sobre la PÁGINA, no dentro de la tarjeta. Encerrado en la tarjeta (que
   tiene `overflow: hidden`) el confeti caía dentro de una caja de 200px, que
   es buena parte de por qué se veía apretado y falso. */
.tk-lluvia {
  position: fixed;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  z-index: 1040;
}
.tk-piece {
  position: absolute;
  top: -20px;
  opacity: 0;
  animation-name: tk-fall;
  animation-timing-function: linear;
  /* `forwards` y una sola vez. En bucle infinito —que es como estaba— el
     confeti nunca termina de caer y deja de leerse como una celebración: se
     lee como un fondo animado que se quedó pegado. */
  animation-iteration-count: 1;
  animation-fill-mode: forwards;
}
@keyframes tk-fall {
  0% {
    opacity: 1;
    transform: translateY(0) rotateZ(0deg) rotateX(0deg);
  }
  100% {
    opacity: 0.85;
    /* 110vh: cruza la pantalla entera y sale por abajo. El giro lo pone cada
       pieza con su propia `--tk-giro`, y el rotateX le da el volteo que hace
       que parezca papel y no un rectángulo de color. */
    transform: translateY(110vh) rotateZ(var(--tk-giro, 720deg)) rotateX(360deg);
  }
}
/* Quien pidió menos movimiento no necesita 80 papeles cayéndole encima. */
@media (prefers-reduced-motion: reduce) {
  .tk-lluvia {
    display: none;
  }
}
</style>
